import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import { MenuItem, TABLE_SETTINGS } from "./menu.models";
import { MAIN_MENU } from "./menu.models";
import { MemberPreferencesRunItTwice } from "../../../shared/models/member-preferences";
import { ManagerService } from "../../../core/services/manager.service";
import { User } from "../../../shared/models/user";
import { SortCards } from "../../../shared/enums/sort-card";
import { ConfigService } from "../../../core/services/config.service";
import { Subscription } from "rxjs";

@Component({
  selector: "app-left-menu",
  templateUrl: "./left-menu.component.html",
  styleUrls: ["./left-menu.component.scss"],
})
export class LeftMenuComponent implements OnInit, OnChanges, OnDestroy {
  @Output() itemClicked = new EventEmitter();
  @Input() user: User;
  @Input() languages;
  @Input() contacts;
  @Input() game;
  @Input() MAIN_MENU = null;
  /** Expand/collapse groups in place instead of navigating into a submenu */
  @Input() useAccordion = false;

  r2t: MemberPreferencesRunItTwice = MemberPreferencesRunItTwice.OFF;
  sortCards: SortCards = SortCards.DEFAULT;
  enumSortCards = SortCards;
  memberPreferencesRunItTwice = MemberPreferencesRunItTwice;
  chipDisplay = "REALISTIC";
  r2tOpen = false;
  sortCardsOpen = true;
  chipsOpen = true;
  expandedIds: Record<string, boolean> = {};

  private stack: { title: string; items: MenuItem[] }[];
  private subscription = new Subscription();
  config: any;

  constructor(
    public managerService: ManagerService,
    private configService: ConfigService,
  ) { }

  ngOnInit(): void {
    this.r2t = this.user?.memberProfile?.Preferences?.RunItTwice;
    this.config = this.configService.config;
    this.stack = [
      { title: "Menu", items: this.getVisibleMenuItems(this.MAIN_MENU) },
    ];
    this.chipDisplay = this.managerService.newChipsDesign$.getValue()
      ? "REALISTIC"
      : "SIMPLE";

    this.subscription.add(
      this.configService.config$.subscribe((config) => {
        if (!config) {
          return;
        }
        this.config = config;
        this.resetStackToCurrentMenu();
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes["MAIN_MENU"]) {
      return;
    }

    // Always reset navigation to root when menu definition changes.
    this.resetStackToCurrentMenu();
  }

  private resetStackToCurrentMenu(): void {
    this.expandedIds = {};
    this.stack = [
      { title: "Menu", items: this.getVisibleMenuItems(this.MAIN_MENU) },
    ];
  }

  private getVisibleMenuItems(items: MenuItem[] | null | undefined): MenuItem[] {
    const menuItems = items ?? [];
    if (!this.config?.removeLogoutButton) {
      return menuItems;
    }
    const hiddenIds = ["logout", "profile-reset", "profile-selfex"];
    return menuItems.filter((item) => !hiddenIds.includes(item.id));
  }

  getIconPath(itemId: string): string | null {
    if (!this.config?.newDesign) {
      return null;
    }

    const iconMap: { [key: string]: string } = {
      // Main menu items
      cashier: "assets/skin/cantobyte/side_menu_cashier.svg",
      tx: "assets/skin/cantobyte/side_menu_cashier.svg",
      gifts: "assets/skin/cantobyte/side_menu_promotions.svg",
      settings: "assets/skin/cantobyte/side_menu_settings.svg",
      profile: "assets/skin/cantobyte/side_menu_profile.svg",
      leaderboard: "assets/skin/cantobyte/side_menu_promotions.svg",
      support: "assets/skin/cantobyte/side_menu_support.svg",
      legal: "assets/skin/cantobyte/side_menu_terms.svg",
      logout: "assets/skin/cantobyte/side_menu_logout.svg",
      shop: "assets/skin/cantobyte/side_menu_promotions.svg",

      // Table settings
      "cards-display": "assets/skin/cantobyte/side_menu_cards.svg",
      "sort-cards": "assets/skin/cantobyte/side_menu_cards.svg",
      "stack-display": "assets/skin/cantobyte/side_menu_stacks.svg",
      "stacks-bb-cash": "assets/skin/cantobyte/side_menu_stacks.svg",
      "stacks-bb-tourn": "assets/skin/cantobyte/side_menu_stacks.svg",
      gameplay: "assets/skin/cantobyte/side_menu_gameplay.svg",
      "table-r2t": "assets/skin/cantobyte/side_menu_gameplay.svg",
      "auto-muck": "assets/skin/cantobyte/side_menu_gameplay.svg",
      "post-bb-auto": "assets/skin/cantobyte/side_menu_gameplay.svg",
      "auto-rebuy": "assets/skin/cantobyte/side_menu_autorebuy.svg",
      "interface-experience": "assets/skin/cantobyte/side_menu_interface.svg",
      "mute-sound": "assets/skin/cantobyte/side_menu_interface.svg",
      "jump-to-table": "assets/skin/cantobyte/side_menu_interface.svg",
      chips: "assets/skin/cantobyte/side_menu_stacks.svg",
      "swipe-table": "assets/skin/cantobyte/side_menu_interface.svg",
      "show-empty-seat": "assets/skin/cantobyte/side_menu_interface.svg",
    };

    return iconMap[itemId] || null;
  }

  get title() {
    return this.stack[this.stack.length - 1].title;
  }

  get items() {
    const rootItems = this.getVisibleMenuItems(
      this.stack[this.stack.length - 1].items,
    );

    if (!this.useAccordion) {
      return rootItems;
    }

    const flattened: MenuItem[] = [];
    for (const item of rootItems) {
      flattened.push(item);
      if (item.children && this.expandedIds[item.id]) {
        flattened.push(...this.getVisibleMenuItems(item.children));
      }
    }
    return flattened;
  }

  get canGoBack() {
    return this.stack.length > 1;
  }

  get logoutItem(): MenuItem {
    return { id: "logout", label: "Logout" };
  }

  isExpanded(itemId: string): boolean {
    return !!this.expandedIds[itemId];
  }

  isNestedItem(item: MenuItem): boolean {
    if (!this.useAccordion) {
      return false;
    }
    const rootItems = this.getVisibleMenuItems(
      this.stack[this.stack.length - 1].items,
    );
    return !rootItems.some((root) => root.id === item.id);
  }

  onItemClick(item: MenuItem, value?: any) {
    if (item.children) {
      if (this.useAccordion) {
        this.expandedIds = {
          ...this.expandedIds,
          [item.id]: !this.expandedIds[item.id],
        };
        return;
      }
      this.stack.push({
        title: item.label,
        items: this.getVisibleMenuItems(item.children),
      });
      return;
    }
    this.itemClicked.emit({ itemId: item.id, value: value });
  }

  goBack() {
    if (this.stack.length > 1) this.stack.pop();
  }
}
